import { useState } from 'react';
import { format, addDays as addDaysFns, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon, Clock, Globe } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { DateFormat, TimeFormat } from '@/core/stores/ui.store';
import { useUIStore } from '@/core/stores/ui.store';
import { Calendar } from '@/shared/components/ui/Calendar';
import { Switch } from '@/shared/components/ui/switch';
import type { PlannedDate } from '@/shared/types/datetime';
import type { TaskStatusType } from '../../types';
import {
  arePlannedDatesEqual,
  formatPlannedDate,
  shortTimeZoneLabel,
} from '@/shared/lib/datetime/planned-date';
import { DateDisplay } from '../DateDisplay';
import { DateTimeInputGroup } from './DateTimeInputGroup';

/** Internal mask vocabulary the text inputs understand. */
type MaskFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/** Map the store's date-fns tokens to the text-mask vocabulary. */
function toMaskFormat(format: DateFormat): MaskFormat {
  switch (format) {
    case 'MM/dd/yyyy':
    case 'MM-dd-yyyy':
      return 'MM/DD/YYYY';
    case 'yyyy-MM-dd':
      return 'YYYY-MM-DD';
    default:
      // dd/MM/yyyy · dd-MM-yyyy · dd.MM.yyyy · d MMM yyyy
      return 'DD/MM/YYYY';
  }
}

function DateRangeDisplay({
  startDate,
  endDate,
  statusType,
}: {
  startDate?: PlannedDate | null;
  endDate?: PlannedDate | null;
  statusType: TaskStatusType;
}) {
  const { t } = useTranslation('tasks');

  if (!startDate && !endDate) {
    return <span className="text-xs text-muted-foreground">{t('listView.noDate')}</span>;
  }

  if (startDate && endDate) {
    return (
      <span className="inline-flex items-center gap-1">
        <DateDisplay date={startDate} variant="start" statusType={statusType} />
        <span className="text-muted-foreground">→</span>
        <DateDisplay date={endDate} variant="due" statusType={statusType} />
      </span>
    );
  }

  return startDate ? (
    <DateDisplay date={startDate} variant="start" statusType={statusType} />
  ) : (
    <DateDisplay date={endDate} variant="due" statusType={statusType} />
  );
}

// ── Format helpers ──────────────────────────────────────────────────────────

function isoToDisplay(iso: string, mask: MaskFormat): string {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  if (!yyyy || !mm || !dd) return '';
  switch (mask) {
    case 'DD/MM/YYYY':
      return `${dd}/${mm}/${yyyy}`;
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`;
    default:
      return iso;
  }
}

function displayToIso(display: string, mask: MaskFormat): string | null {
  switch (mask) {
    case 'DD/MM/YYYY': {
      const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
    }
    case 'MM/DD/YYYY': {
      const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      return m ? `${m[3]}-${m[1]}-${m[2]}` : null;
    }
    default:
      return display.match(/^\d{4}-\d{2}-\d{2}$/) ? display : null;
  }
}

function applyDateMask(raw: string, mask: MaskFormat, prev: string): string {
  if (raw.length < prev.length) return raw;
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (mask === 'YYYY-MM-DD') {
    let r = digits.slice(0, 4);
    if (digits.length > 4) r += '-' + digits.slice(4, 6);
    if (digits.length > 6) r += '-' + digits.slice(6, 8);
    return r;
  }
  let r = digits.slice(0, 2);
  if (digits.length > 2) r += '/' + digits.slice(2, 4);
  if (digits.length > 4) r += '/' + digits.slice(4, 8);
  return r;
}

function formatPresetDate(days: number): string {
  return format(addDaysFns(new Date(), days), 'EEE d MMM', { locale: es }).replace(/\./g, '');
}

function formatHeaderDate(
  value: PlannedDate | null,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
): string {
  if (!value) return '';
  return formatPlannedDate(value, { dateFormat, timeFormat, showZoneLabel: true });
}

// ── Presets ─────────────────────────────────────────────────────────────────

const PRESETS = [
  { labelKey: 'datePicker.presetToday', days: 0 },
  { labelKey: 'datePicker.presetTomorrow', days: 1 },
  { labelKey: 'datePicker.presetNextWeek', days: 7 },
  { labelKey: 'datePicker.presetTwoWeeks', days: 14 },
] as const;

// ── Component ────────────────────────────────────────────────────────────────

interface TaskDatePickerProps {
  startDate?: PlannedDate | null;
  endDate?: PlannedDate | null;
  statusType: TaskStatusType;
  onSave: (start: PlannedDate | null, end: PlannedDate | null) => void;
  disabled?: boolean;
  /** 'default' = w-80 (320px) · 'wide' = w-96 (384px) */
  size?: 'default' | 'wide';
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
}

export function TaskDatePicker({
  startDate,
  endDate,
  statusType,
  onSave,
  disabled,
  size = 'default',
  dateFormat = 'dd/MM/yyyy',
  timeFormat = 'HH:mm',
}: TaskDatePickerProps) {
  const { t } = useTranslation('tasks');
  const userTimezone = useUIStore((s) => s.timezone);
  const [open, setOpen] = useState(false);

  const mask = toMaskFormat(dateFormat);

  const [localStartDate, setLocalStartDate] = useState('');
  const [localEndDate, setLocalEndDate] = useState('');
  const [localStartTime, setLocalStartTime] = useState('');
  const [localEndTime, setLocalEndTime] = useState('');

  const [displayStartDate, setDisplayStartDate] = useState('');
  const [displayEndDate, setDisplayEndDate] = useState('');

  const [endDateEnabled, setEndDateEnabled] = useState(false);
  const [includeTime, setIncludeTime] = useState(false);
  const [zoneEnabled, setZoneEnabled] = useState(false);
  const [timeZoneId, setTimeZoneId] = useState(userTimezone);
  const [activeField, setActiveField] = useState<'start' | 'end'>('start');

  // ── Build a PlannedDate from the local floating components ───────────────────

  function buildPlanned(dateStr: string, timeStr: string): PlannedDate | null {
    if (!dateStr) return null;
    const planned: PlannedDate = { date: dateStr };
    if (includeTime && timeStr) {
      planned.time = timeStr;
      if (zoneEnabled && timeZoneId) {
        planned.timeZoneId = timeZoneId;
      }
    }
    return planned;
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  function handleOpenChange(isOpen: boolean) {
    if (isOpen) {
      const sd = startDate?.date ?? '';
      const st24 = startDate?.time ?? '';
      const ed = endDate?.date ?? '';
      const et24 = endDate?.time ?? '';

      const originalHasTime = startDate?.time != null || endDate?.time != null;
      const originalZone = startDate?.timeZoneId ?? endDate?.timeZoneId ?? null;

      setLocalStartDate(sd);
      setLocalEndDate(ed);
      setLocalStartTime(st24);
      setLocalEndTime(et24);
      setDisplayStartDate(isoToDisplay(sd, mask));
      setDisplayEndDate(isoToDisplay(ed, mask));
      setEndDateEnabled(!!endDate);
      setIncludeTime(originalHasTime);
      setZoneEnabled(originalZone != null);
      setTimeZoneId(originalZone ?? userTimezone);
      setActiveField(endDate ? 'end' : 'start');
    } else {
      const start = localStartDate ? buildPlanned(localStartDate, localStartTime) : null;
      const end = endDateEnabled ? buildPlanned(localEndDate, localEndTime) : null;

      // Emit only on a real change — floating values compared structurally.
      const startChanged = !arePlannedDatesEqual(start, startDate ?? null);
      const endChanged = !arePlannedDatesEqual(end, endDate ?? null);

      if (startChanged || endChanged) {
        onSave(start, end);
      }
    }
    setOpen(isOpen);
  }

  // ── Calendar ──────────────────────────────────────────────────────────────

  function handleCalendarSelect(date: Date | undefined) {
    if (!date) return;
    const iso = format(date, 'yyyy-MM-dd');
    const disp = isoToDisplay(iso, mask);

    if (activeField === 'start') {
      if (localEndDate && iso > localEndDate) {
        setLocalStartDate(localEndDate);
        setDisplayStartDate(isoToDisplay(localEndDate, mask));
        setLocalEndDate(iso);
        setDisplayEndDate(disp);
      } else {
        setLocalStartDate(iso);
        setDisplayStartDate(disp);
      }
    } else {
      if (endDateEnabled && localStartDate && iso < localStartDate) {
        setLocalEndDate(localStartDate);
        setDisplayEndDate(isoToDisplay(localStartDate, mask));
        setLocalStartDate(iso);
        setDisplayStartDate(disp);
      } else {
        setLocalEndDate(iso);
        setDisplayEndDate(disp);
      }
    }
  }

  // ── Presets ───────────────────────────────────────────────────────────────

  function applyPreset(days: number) {
    const iso = format(addDaysFns(new Date(), days), 'yyyy-MM-dd');
    const disp = isoToDisplay(iso, mask);
    if (activeField === 'end' && endDateEnabled) {
      if (localStartDate && iso < localStartDate) {
        setLocalEndDate(localStartDate);
        setDisplayEndDate(isoToDisplay(localStartDate, mask));
        setLocalStartDate(iso);
        setDisplayStartDate(disp);
      } else {
        setLocalEndDate(iso);
        setDisplayEndDate(disp);
      }
    } else {
      if (endDateEnabled && localEndDate && iso > localEndDate) {
        setLocalStartDate(localEndDate);
        setDisplayStartDate(isoToDisplay(localEndDate, mask));
        setLocalEndDate(iso);
        setDisplayEndDate(disp);
      } else {
        setLocalStartDate(iso);
        setDisplayStartDate(disp);
      }
      setActiveField('start');
    }
  }

  // ── Text input handlers ───────────────────────────────────────────────────

  function handleDateChange(
    raw: string,
    prev: string,
    setDisp: (s: string) => void,
    setIso: (s: string) => void,
  ) {
    const masked = applyDateMask(raw, mask, prev);
    setDisp(masked);
    const iso = displayToIso(masked, mask);
    if (iso) setIso(iso);
  }

  function handleStartDateBlur() {
    if (!displayToIso(displayStartDate, mask)) {
      setDisplayStartDate(localStartDate ? isoToDisplay(localStartDate, mask) : '');
      return;
    }
    if (endDateEnabled && localEndDate && localStartDate > localEndDate) {
      setLocalStartDate(localEndDate);
      setDisplayStartDate(isoToDisplay(localEndDate, mask));
    }
  }

  function handleEndDateBlur() {
    if (!displayToIso(displayEndDate, mask)) {
      setDisplayEndDate(localEndDate ? isoToDisplay(localEndDate, mask) : '');
      return;
    }
    if (localStartDate && localEndDate < localStartDate) {
      setLocalEndDate(localStartDate);
      setDisplayEndDate(isoToDisplay(localStartDate, mask));
    }
  }

  // ── Toggles ───────────────────────────────────────────────────────────────

  function handleEndDateToggle(checked: boolean) {
    setEndDateEnabled(checked);
    if (checked) {
      setActiveField('end');
      if (!localEndDate && localStartDate) {
        const next = format(addDaysFns(parseISO(localStartDate), 1), 'yyyy-MM-dd');
        setLocalEndDate(next);
        setDisplayEndDate(isoToDisplay(next, mask));
      }
    } else {
      setLocalEndDate('');
      setDisplayEndDate('');
      setLocalEndTime('');
      setActiveField('start');
    }
  }

  function handleIncludeTimeToggle(checked: boolean) {
    setIncludeTime(checked);
    // A zone is meaningless without a time — drop it when time is turned off.
    if (!checked) setZoneEnabled(false);
  }

  // ── Remove ────────────────────────────────────────────────────────────────

  function handleRemove() {
    setLocalStartDate('');
    setLocalEndDate('');
    setLocalStartTime('');
    setLocalEndTime('');
    setDisplayStartDate('');
    setDisplayEndDate('');
    setOpen(false);
    onSave(null, null);
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const parsedStart = localStartDate ? new Date(localStartDate + 'T12:00:00') : undefined;
  const parsedEnd =
    endDateEnabled && localEndDate ? new Date(localEndDate + 'T12:00:00') : undefined;
  const hasDates = !!(localStartDate || localEndDate);
  const headerValue: PlannedDate | null =
    localEndDate || localStartDate ? { date: localEndDate || localStartDate } : null;

  const calendarKey = (() => {
    const anchor = (activeField === 'end' ? parsedEnd : undefined) ?? parsedStart;
    if (!anchor) return 'empty';
    return `${anchor.getFullYear()}-${anchor.getMonth()}`;
  })();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Trigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="tasks-date-picker-trigger"
          className="flex items-center text-xs transition-opacity hover:opacity-70"
          onClick={(e) => e.stopPropagation()}
        >
          <DateRangeDisplay startDate={startDate} endDate={endDate} statusType={statusType} />
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/20" />
        <DialogPrimitive.Content
          data-testid="tasks-date-picker-popover"
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border bg-background shadow-lg outline-none',
            'p-0',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            size === 'wide' ? 'w-80 sm:w-125' : 'w-80 sm:w-110',
          )}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <DialogPrimitive.Title className="sr-only">{t('datePicker.title')}</DialogPrimitive.Title>
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2.5">
            <span className="text-sm font-semibold">{t('datePicker.title')}</span>
            {headerValue && (
              <span className="text-xs font-medium text-primary">
                {formatHeaderDate(headerValue, dateFormat, timeFormat)}
              </span>
            )}
          </div>

          {/* Body: stacked on mobile, two-column on sm+ */}
          <div className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:divide-x">
            {/* Left: quick access presets */}
            <div className="border-b p-3 sm:border-b-0">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('datePicker.presets')}
              </p>
              <div className="flex flex-col gap-0.5">
                {PRESETS.map((preset) => (
                  <button
                    type="button"
                    key={preset.labelKey}
                    onClick={() => applyPreset(preset.days)}
                    className="flex flex-col rounded px-2 py-1.5 text-left transition-colors hover:bg-accent"
                  >
                    <span className="text-xs">{t(preset.labelKey)}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatPresetDate(preset.days)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: manual inputs */}
            <div className="flex flex-col gap-2.5 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('datePicker.writeDate')}
              </p>

              {/* Start date + optional time */}
              <div>
                <p className="mb-1 text-[10px] text-muted-foreground">
                  {t('datePicker.startDate')}
                </p>
                <DateTimeInputGroup
                  field="start"
                  activeField={activeField}
                  dateTestId="tasks-datepicker-date-input"
                  timeTestId="tasks-datepicker-time-input"
                  displayDate={displayStartDate}
                  time={localStartTime}
                  dateFormat={mask}
                  timeFormat={timeFormat}
                  includeTime={includeTime}
                  timeLabelKey="datePicker.startTime"
                  onDateChange={(e) =>
                    handleDateChange(
                      e.target.value,
                      displayStartDate,
                      setDisplayStartDate,
                      setLocalStartDate,
                    )
                  }
                  onDateBlur={handleStartDateBlur}
                  onDateFocus={() => setActiveField('start')}
                  onTimeChange={setLocalStartTime}
                />
              </div>

              {/* End date + optional time */}
              <div>
                <p className="mb-1 text-[10px] text-muted-foreground">{t('datePicker.endDate')}</p>
                <DateTimeInputGroup
                  field="end"
                  activeField={activeField}
                  dateTestId="tasks-datepicker-end-input"
                  timeTestId="tasks-datepicker-end-time"
                  displayDate={displayEndDate}
                  time={localEndTime}
                  dateFormat={mask}
                  timeFormat={timeFormat}
                  disabled={!endDateEnabled}
                  includeTime={includeTime}
                  timeLabelKey="datePicker.endTime"
                  onDateChange={(e) =>
                    handleDateChange(
                      e.target.value,
                      displayEndDate,
                      setDisplayEndDate,
                      setLocalEndDate,
                    )
                  }
                  onDateBlur={handleEndDateBlur}
                  onDateFocus={() => setActiveField('end')}
                  onTimeChange={setLocalEndTime}
                />
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="border-t">
            <Calendar
              key={calendarKey}
              data-testid="tasks-date-picker-calendar"
              rangeStart={parsedStart}
              rangeEnd={parsedEnd}
              onSelect={handleCalendarSelect}
            />
          </div>

          {/* Footer toggles */}
          <div className="flex flex-col gap-2 border-t px-3 py-2">
            <div className="flex items-center gap-4">
              <label className="flex cursor-pointer items-center gap-1.5">
                <CalendarIcon className="size-3 shrink-0 text-muted-foreground" />
                <span className="text-xs">{t('datePicker.endDateToggle')}</span>
                <Switch
                  data-testid="tasks-date-picker-switch-enddate"
                  size="sm"
                  checked={endDateEnabled}
                  onCheckedChange={handleEndDateToggle}
                />
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <Clock className="size-3 shrink-0 text-muted-foreground" />
                <span className="text-xs">{t('datePicker.includeTime')}</span>
                <Switch
                  data-testid="tasks-date-picker-switch-time"
                  size="sm"
                  checked={includeTime}
                  onCheckedChange={handleIncludeTimeToggle}
                />
              </label>
            </div>

            {/* Fixed time zone — only meaningful when a time is set */}
            {includeTime && (
              <label className="flex cursor-pointer items-center gap-1.5">
                <Globe className="size-3 shrink-0 text-muted-foreground" />
                <span className="text-xs">{t('datePicker.fixedTimeZone')}</span>
                <Switch
                  data-testid="tasks-datepicker-zone-toggle"
                  size="sm"
                  checked={zoneEnabled}
                  onCheckedChange={setZoneEnabled}
                />
                {zoneEnabled && (
                  <span className="text-[10px] text-muted-foreground">
                    {shortTimeZoneLabel(timeZoneId)}
                  </span>
                )}
              </label>
            )}
            {!zoneEnabled && includeTime && (
              <p className="text-[10px] text-muted-foreground">
                {t('datePicker.floatingTimeHint')}
              </p>
            )}
          </div>

          {/* Remove link */}
          {hasDates && (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                data-testid="tasks-date-picker-remove"
                onClick={handleRemove}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t('datePicker.removeDates')}
              </button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
